// src/components/ChapterEditor.js
import React, { useState, useMemo, useRef, useCallback } from 'react';
import ReactQuill, { Quill } from 'react-quill-new'; // Using react-quill-new
import 'react-quill-new/dist/quill.snow.css';
import axiosInstance from '../utils/axios'; // Your configured axios instance
import { registerQuillCustoms } from '../utils/quillCustoms';
import { ProgressBar, Alert, Modal } from 'react-bootstrap';

// Call the registration function once when this module is loaded
registerQuillCustoms();


const ChapterEditor = ({ initialValue = '', onContentChange }) => {
  const [editorValue, setEditorValue] = useState(initialValue); // Can be Delta or HTML string
  const quillRef = useRef(null);

  // New state variables for upload progress
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  const handleChange = (content, delta, source, editor) => {
    setEditorValue(editor.getContents()); // Store Delta
    if (onContentChange) {
      onContentChange({ html: editor.getHTML(), delta: editor.getContents() });
    }
  };

  // Image Handler (from previous setup)
  const imageHandler = useCallback(() => {
    if (!quillRef.current) return;
    const editor = quillRef.current.getEditor();
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files[0];
      if (file && editor) {
        const formData = new FormData();
        formData.append('image', file);
        try {
          const response = await axiosInstance.post('/media/upload-image/', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          const imageUrl = response.data.imageUrl;
          const range = editor.getSelection(true);
          editor.insertEmbed(range.index, 'image', imageUrl);
          editor.setSelection(range.index + 1);
        } catch (error) {
          console.error('Image upload failed:', error.response?.data || error.message);
          alert(`Image upload failed: ${error.response?.data?.error || error.message}`);
        }
      }
    };
  }, []);

  // --- New Video Handler ---
  const videoHandler = useCallback(() => {
    if (!quillRef.current) return;
    const editor = quillRef.current.getEditor();
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'video/mp4'); // Specifically for MP4
    input.click();

    input.onchange = async () => {
      const file = input.files[0];
      if (file && editor) {
        const formData = new FormData();
        formData.append('video', file); // Key 'video' must match backend VideoUploadView

        // Optional: You can add a loading indicator here
        // e.g., editor.formatText(range.index, 1, { 'loading-video': true });

        try {
          const response = await axiosInstance.post('/media/upload-video/', formData, { // New endpoint
            headers: {
              'Content-Type': 'multipart/form-data',
            },
            onUploadProgress: (progressEvent) => {
              setIsUploading(true);
              console.log(progressEvent)
              const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setUploadProgress(percentCompleted);
            },
          });

          const videoUrl = response.data.videoUrl; // Expect 'videoUrl' from backend
          const range = editor.getSelection(true); // Get current cursor position

          // Quill's built-in video module inserts a <video> tag when given a direct URL
          editor.insertEmbed(range.index, 'customVideo', videoUrl);
          editor.setSelection(range.index + 1); // Move cursor after the inserted video

        } catch (error) {
          console.error('Video upload failed:', error.response?.data || error.message);
          alert(`Video upload failed: ${error.response?.data?.error || error.message}`);
          // Handle error (e.g., show a notification to the user)
        } finally {
          setIsUploading(false);
        }
      }
    };
  }, []); // quillRef is stable, no need to list as dependency

  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
        [{ 'font': [] }],
        ['bold', 'italic', 'underline', 'strike'],
        ['blockquote', 'code-block'],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        [{ 'script': 'sub' }, { 'script': 'super' }],
        [{ 'indent': '-1' }, { 'indent': '+1' }],
        [{ 'direction': 'rtl' }],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'align': [] }],
        ['link', 'image', 'video'], // 'video' button will now use our custom handler
        ['clean']
      ],
      handlers: {
        'image': imageHandler,
        'video': videoHandler, // <<< Assign custom video handler
      }
    },
    // ... other modules like imageResize ...
  }), [imageHandler, videoHandler]);

  const formats = [
    'header', 'font',
    'bold', 'italic', 'underline', 'strike', 'blockquote', 'code-block',
    'list', 'bullet', 'indent', 'script',
    'link', 'image', 'video', 'customVideo', // <<< Ensure 'video' format is listed
    'color', 'background', 'align', 'direction'
  ];

  // Handle initialValue: if it's a Delta object from DB, pass it directly
  // If it's an HTML string for a new chapter, it's also fine.
  // ReactQuill `value` prop typically expects Delta or HTML string.
  // `defaultValue` can be used for uncontrolled component if initialValue is only for the first time.
  // For controlled component with Delta:
  // const [deltaValue, setDeltaValue] = useState(initialValueIsDelta ? initialValue : new Delta());
  // onChange={(content, delta, source, editor) => { setDeltaValue(delta); onContentChange(...) }}

  return (
    <> {/* Use React Fragment to hold editor and modal */}
      {/* Upload Progress Modal */}
      <Modal
        show={isUploading}
        centered
        backdrop="static"   // Prevents closing on backdrop click
        keyboard={false}    // Prevents closing with Esc key
        style={{ zIndex: 2050 }} // Ensure it appears above other content
      >
        <Modal.Body className="text-center">
          <div className="bg-white p-4 rounded shadow-lg">
            <h5 className="mb-3">Uploading File...</h5>
            <ProgressBar
              animated
              now={uploadProgress}
              label={`${uploadProgress}%`}
              variant="success"
              style={{ height: '25px', fontSize: '1rem' }}
            />
            <p className="text-muted mt-2">Please wait until the upload is complete.</p>
          </div>
        </Modal.Body>
      </Modal>

      {/* Upload Error Alert */}
      {uploadError && (
        <Alert variant="danger" onClose={() => setUploadError(null)} dismissible className="py-2 px-3 text-sm mt-2">
          {uploadError}
        </Alert>
      )}

      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={editorValue}
        onChange={handleChange}
        modules={modules}
        formats={formats}
        placeholder="Start writing your amazing chapter..."
        style={{ minHeight: '300px', backgroundColor: 'white' }}
        readOnly={isUploading} // Disable editor during upload
      />
    </>
  );
};

export default ChapterEditor;